// "Heute handeln" + Netto-4W-Brücke — reine Ableitungen aus bereits
// geladenen Dashboard-Queries (Makeover 2026-07: proaktiv statt Kachel-Zoo).
//
// Keine neuen Endpoints: alle Inputs existieren im DashboardPage-Querieset.
// Bewusst pure functions → vitest-bar, kein React-Import.

import type {
  AnomalyEventsResponse,
  CashTotalKpi,
  ComplianceResponse,
  ForderungenResponse,
  LiquiForecastResponse,
  MahnungenEingegangenResponse,
  OutflowForecastResponse,
  PipelineResponse,
  UmsatzLiveResponse,
} from "./api";
import type { Severity } from "./severity";

// M-B2: "kanal" = Sprung zur Kanal-Inspektion (Spülung = der EINE
// Mahnlauf-CTA); die Zone-A-Action verlinkt nur noch dorthin.
export type ActionDrawer = "mahnungen" | "cash" | "outflow" | "kanal";

export interface TodayAction {
  id: string;
  severity: Severity;
  titel: string;
  detail: string | null;
  betrag_eur: number | null;
  /** react-router-Pfad, wenn es eine Ziel-Page gibt. */
  to: string | null;
  /** Dashboard-Drawer, wenn das Detail dort lebt (nur Desktop). */
  drawer: ActionDrawer | null;
}

export interface DeriveActionsInput {
  cash?: CashTotalKpi | null;
  mahnungen?: MahnungenEingegangenResponse | null;
  forderungen?: ForderungenResponse | null;
  umsatzLive?: UmsatzLiveResponse | null;
  compliance?: ComplianceResponse | null;
  anomaly?: AnomalyEventsResponse | null;
  pipeline?: PipelineResponse | null;
}

const num = (v: string | null | undefined): number | null => {
  if (v == null || v === "") return null;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const SEV_ORDER: Record<Severity, number> = { critical: 0, warn: 1, ok: 2 };

export function deriveTodayActions(input: DeriveActionsInput): TodayAction[] {
  const actions: TodayAction[] = [];

  // 1) Banking-Salden fehlen — solange saldo_snapshot leer ist, ist das
  //    die wichtigste strukturelle Aktion (EBICS-Go-Live).
  if (input.cash?.status === "no_data") {
    actions.push({
      id: "banking-missing",
      severity: "critical",
      titel: "Bank-Salden fehlen — EBICS aktivieren",
      detail:
        "Briefe faxen → activate (Runbook ebics-go-live) — oder Salden einmalig manuell eintragen (/admin/cash-eintrag)",
      betrag_eur: null,
      to: "/admin/cash-eintrag",
      drawer: "cash",
    });
  }

  // 2) Mahnungen GEGEN uns (Reputations-/Kostenrisiko zuerst).
  const mahnOffen = input.mahnungen?.n_offen ?? 0;
  if (mahnOffen > 0) {
    const total = num(input.mahnungen?.total_offen_eur);
    actions.push({
      id: "mahnungen-gegen-uns",
      severity: total != null && total > 5_000 ? "critical" : "warn",
      titel: `${mahnOffen} Mahnung${mahnOffen === 1 ? "" : "en"} gegen uns offen`,
      detail: input.mahnungen?.items?.[0]
        ? `Zuerst: ${input.mahnungen.items[0].glaeubiger} (${input.mahnungen.items[0].firma_display})`
        : null,
      betrag_eur: total,
      to: null,
      drawer: "mahnungen",
    });
  }

  // 3) Mahnlauf fuer UNSERE ueberfaelligen Forderungen (DrainX + G+K;
  //    InfraX macht Mahnwesen separat — bewusst ausgeklammert).
  const { sum: ueberfaelligSum, parts: ueberfaelligParts } =
    deriveUeberfaelligDxGk(input.umsatzLive);
  if (ueberfaelligSum > 0) {
    actions.push({
      id: "mahnlauf-faellig",
      severity: ueberfaelligSum > 25_000 ? "critical" : "warn",
      titel: "Mahnlauf fällig: überfällige Forderungen",
      detail: `${ueberfaelligParts.join(" · ")} → Kanal-Spülung starten`,
      betrag_eur: ueberfaelligSum,
      to: null,
      drawer: "kanal",
    });
  } else if ((input.forderungen?.n_overdue ?? 0) > 0) {
    actions.push({
      id: "mahnlauf-faellig-count",
      severity: "warn",
      titel: `${input.forderungen!.n_overdue} überfällige Forderungen (DrainX)`,
      detail: "Kanal-Spülung starten",
      betrag_eur: null,
      to: null,
      drawer: "kanal",
    });
  }

  // 4) Steuer-/Compliance-Termine ≤ 7 Tage (max 2, naechste zuerst).
  const steuerSoon = (input.compliance?.items ?? [])
    .filter((i) => i.days_until <= 7)
    .slice(0, 2);
  for (const item of steuerSoon) {
    actions.push({
      id: `steuer-${item.datum}-${item.titel}`,
      severity: item.days_until <= 2 ? "critical" : "warn",
      titel:
        item.days_until < 0
          ? `ÜBERFÄLLIG: ${item.titel}`
          : `${item.titel} in ${item.days_until} T`,
      detail: `${item.firma_id} · ${item.datum}`,
      betrag_eur: num(item.betrag_eur),
      to: "/steuern",
      drawer: null,
    });
  }

  // 5) Kritische Anomalien.
  if ((input.anomaly?.n_critical_open ?? 0) > 0) {
    const top = input.anomaly?.items?.[0];
    actions.push({
      id: "anomaly-crit",
      severity: "critical",
      titel: `${input.anomaly!.n_critical_open} kritische Anomalie(n)`,
      detail: top ? top.titel : null,
      betrag_eur: null,
      to: null,
      drawer: null,
    });
  }

  // 6) Angebots-Nachfass (Tooltime-Pipeline, stale offers).
  const staleOffers = input.pipeline?.n_offers_stale ?? 0;
  if (staleOffers > 0) {
    actions.push({
      id: "angebote-nachfassen",
      severity: staleOffers > 10 ? "warn" : "ok",
      titel: `${staleOffers} Angebote ohne Bewegung`,
      detail: "Nachfass-Lauf (Angebots-Radar) anstoßen",
      betrag_eur: null,
      to: null,
      drawer: null,
    });
  }

  // 8) Datenfrische Tooltime-Snapshot (Basis fast aller Zahlen hier).
  const age = input.umsatzLive?.age_hours;
  if (
    input.umsatzLive?.available &&
    ((age != null && age > 8 * 24) || input.umsatzLive.is_bootstrap_only === true)
  ) {
    actions.push({
      id: "tooltime-stale",
      severity: "warn",
      titel: `Tooltime-Daten ${age != null ? Math.round(age / 24) : "?"} Tage alt`,
      detail: "Cowork: Tooltime-Snapshot-Pull anstoßen",
      betrag_eur: null,
      to: null,
      drawer: null,
    });
  }

  return actions.sort((a, b) => {
    const sev = SEV_ORDER[a.severity] - SEV_ORDER[b.severity];
    if (sev !== 0) return sev;
    return (b.betrag_eur ?? -1) - (a.betrag_eur ?? -1);
  });
}

/** Ueberfaellige Forderungen DrainX + G+K aus dem Tooltime-Snapshot.
 *  InfraX bleibt draussen (R1: kein Konzern-Aggregat; eigenes Mahnwesen).
 *  Geteilte Quelle fuer die Mahnlauf-Action UND den LageHeader-Micro-KPI. */
export function deriveUeberfaelligDxGk(umsatzLive?: UmsatzLiveResponse | null): {
  sum: number;
  parts: string[];
} {
  const parts: string[] = [];
  let sum = 0;
  for (const f of umsatzLive?.firmas ?? []) {
    if (f.firma_id !== "drainx" && f.firma_id !== "gundk") continue;
    const v = num(f.forderungen_ueberfaellig_brutto_eur);
    if (v != null && v > 0) {
      sum += v;
      parts.push(
        `${f.firma_id === "drainx" ? "DrainX" : "G+K"} ${Math.round(v).toLocaleString("de-DE")} €`,
      );
    }
  }
  return { sum, parts };
}

export function deriveActionsSeverity(actions: TodayAction[]): Severity {
  if (actions.some((a) => a.severity === "critical")) return "critical";
  if (actions.some((a) => a.severity === "warn")) return "warn";
  return "ok";
}

// ----------------------------------------------------------------------
// Netto-4W-Brücke: Cash + erwartete Eingänge 4W − geplante Ausgänge 4W.
// Die eine Berechnung, die "reicht es?" in einer Zahl beantwortet.
// ----------------------------------------------------------------------

export interface Netto4W {
  cash_eur: number | null;
  eingang_4w_eur: number | null;
  ausgang_4w_eur: number | null;
  /** Cash + Eingang − Ausgang. Ohne Cash (Banking-Setup offen): nur
   *  Eingang − Ausgang, gekennzeichnet via cash_eur === null. */
  netto_eur: number | null;
  severity: Severity;
  hinweis: string | null;
}

export function deriveNetto4W(
  cash?: CashTotalKpi | null,
  liqui?: LiquiForecastResponse | null,
  outflow?: OutflowForecastResponse | null,
): Netto4W {
  const cashNum = cash?.status === "ok" ? num(cash.total_eur) : null;

  let eingang: number | null = null;
  const wochen = liqui?.wochen ?? [];
  if (wochen.length > 0) {
    eingang = 0;
    for (const w of wochen.slice(0, 4)) eingang += num(w.eingang_eur) ?? 0;
  }

  const ausgang = outflow?.available ? num(outflow.next_28_sum_eur) : null;

  let netto: number | null = null;
  if (eingang != null || ausgang != null || cashNum != null) {
    netto = (cashNum ?? 0) + (eingang ?? 0) - (ausgang ?? 0);
  }

  let severity: Severity = "ok";
  let hinweis: string | null = null;
  if (netto == null) {
    severity = "warn";
    hinweis = "Keine Datenbasis (Banking + Forecast fehlen)";
  } else if (cashNum == null) {
    severity = "warn";
    hinweis = "ohne Start-Cash — Banking-Setup (EBICS) ausstehend";
  } else if (netto < 0) {
    severity = "critical";
    hinweis = "Unterdeckung in 4 Wochen — Eingänge beschleunigen/Ausgaben schieben";
  } else if (ausgang != null && netto < ausgang * 0.25) {
    severity = "warn";
    hinweis = "Puffer < 25 % der 4W-Ausgänge";
  }

  return {
    cash_eur: cashNum,
    eingang_4w_eur: eingang,
    ausgang_4w_eur: ausgang,
    netto_eur: netto,
    severity,
    hinweis,
  };
}

// ----------------------------------------------------------------------
// Lage-Satz (Ebene 1, DESIGN-V3-LAGEBILD §3): EIN Serif-Satz beantwortet
// "wie stehe ich da / was tue ich" — der Mercury-Moment. Pure function,
// gerendert vom LageHeader.
// ----------------------------------------------------------------------

const fmtEur = (v: number): string => `${Math.round(v).toLocaleString("de-DE")} €`;

export function buildLageSatz(actions: TodayAction[], netto: Netto4W): string {
  const dringend = actions.filter((a) => a.severity !== "ok").length;
  const crit = actions.filter((a) => a.severity === "critical").length;

  let lage: string;
  if (dringend === 0) {
    lage = "Nichts brennt — ruhige Lage.";
  } else if (crit === dringend && crit > 0) {
    // AUFTRAG M B6: alle dringenden Punkte sind kritisch — "2 Punkte …,
    // 2 davon kritisch" wäre doppelt gemoppelt.
    lage = `${crit} ${crit === 1 ? "kritischer Punkt braucht" : "kritische Punkte brauchen"} dich heute — sonst ruhig.`;
  } else if (crit > 0) {
    lage = `${dringend} ${dringend === 1 ? "Punkt braucht" : "Punkte brauchen"} dich heute, ${crit} davon kritisch.`;
  } else {
    lage = `${dringend} ${dringend === 1 ? "Punkt braucht" : "Punkte brauchen"} dich heute — sonst ruhig.`;
  }

  if (netto.netto_eur != null) {
    const vz = netto.netto_eur < 0 ? "−" : "";
    return `${lage} Netto 4 Wochen: ${vz}${fmtEur(Math.abs(netto.netto_eur))}.`;
  }
  return lage;
}
